import React, {
  RefObject,
  useMemo,
  useRef,
} from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import FilerobotImageEditor, {
  getCurrentImgDataFunction,
  TABS,
  TOOLS,
} from 'react-filerobot-image-editor';
import {
  getFileLocalUrlFromEditorRef,
} from 'utils/files';
import type {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import isNil from 'lodash/isNil';

export type EditorImageData = {
  imageBase64?: string;
  imageCanvas?: HTMLCanvasElement | null;
  quality?: number;
};

export type GetImgDataRef =
| RefObject<getCurrentImgDataFunction | null>
| { current?: getCurrentImgDataFunction | null }
| getCurrentImgDataFunction;

const editorTheme = {
  palette: {
    'accent-primary': '#1E84F1',
    'accent-primary-active': '#1E84F1',
    'icons-primary': '#1E84F1',
    'bg-primary-active': '#ECF3FF',
    'text-primary': '#111827',
  },
  typography: {
    fontFamily: 'Montserrat, Arial, sans-serif',
  },
};

type Props = {
  src: string;
  fileName?: string;
  onSave: (file: FileLocalUrl) => Promise<void> | void;
  onClose: () => void;
};

function toEditedName(original?: string, ext = 'png') {
  if (!original) return `image-edited.${ext}`;
  const lastDot = original.lastIndexOf('.');
  const base = lastDot > 0 ? original.substring(0, lastDot) : original;
  return `${base}-edited.${ext}`;
}

export default function ImageEditorDialog({
  src,
  fileName,
  onSave,
  onClose,
}: Props) {
  // Match Upload Media/Capture dialog visual height
  const containerStyles = 'w-full h-[80vh] max-h-[calc(90vh-2rem)] min-h-[28rem]';
  const getImgDataRef = useRef<getCurrentImgDataFunction | null>(null);

  type ImageType = 'png' | 'jpeg' | 'jpg' | 'webp';
  const ALLOWED: ReadonlySet<string> = new Set(['png', 'jpeg', 'jpg', 'webp']);

  // Ensure the editor gets an HTMLImageElement with CORS enabled so reset/history work reliably
  const sourceImage = useMemo(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    return img;
  }, [src]);

  const defaultExt = useMemo<ImageType>(() => {
    const match = /^data:(image\/(png|jpeg|jpg|webp));base64,/.exec(src);
    if (match) {
      const subtype = match[2];
      const normalized = subtype === 'jpeg' ? 'jpg' : subtype;
      return (ALLOWED.has(normalized) ? (normalized as ImageType) : 'png');
    }
    return 'png';
  }, [src]);

  const handleOnSave = async () => {
    try {
      const payload = await getFileLocalUrlFromEditorRef(
        getImgDataRef,
        toEditedName(fileName, defaultExt),
        defaultExt,
        0.92,
      );
      if (!isNil(payload)) {
        await Promise.resolve(onSave(payload));
      }
    } finally {
      onClose();
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit Image"
      maxWidth="xl"
      fullWidth={false}
      disableEnforceFocus
      className="overflow-y-hidden overflow-x-hidden !space-y-0 gap-2 !w-[62rem]"
      content={(
        <div className="flex flex-col gap-3">
          <div className={containerStyles}>
            <FilerobotImageEditor
              theme={editorTheme}
              source={sourceImage}
              onClose={onClose}
              closeAfterSave={false}
              showBackButton
              loadableDesignState={{ imgSrc: src }}
              resetOnImageSourceChange
              defaultSavedImageName={toEditedName(fileName, defaultExt)}
              defaultSavedImageType={defaultExt}
              defaultSavedImageQuality={0.92}
              savingPixelRatio={2}
              previewPixelRatio={window.devicePixelRatio || 1}
              disableSaveIfNoChanges={false}
              removeSaveButton
              getCurrentImgDataFnRef={getImgDataRef}
              tabsIds={[
                TABS.ADJUST,
                TABS.FINETUNE,
                TABS.RESIZE,
                TABS.FILTERS,
              ]}
              defaultTabId={TABS.ADJUST}
              defaultToolId={TOOLS.CROP}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outlined" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOnSave}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    />
  );
}
